import type {
  EnhancerOptions,
  EnhancerOutput,
  RootEnhancer,
} from "../types/enhancer"

export const mediaEnhancer =
  <
    InheritOptions extends EnhancerOptions<RootEnhancer>,
    InheritOutput extends EnhancerOutput<RootEnhancer>,
  >(
    next: (options: InheritOptions) => InheritOutput,
  ) =>
  (options: InheritOptions): InheritOutput => {
    const reader = next(options)

    const IntersectionObserverCtor =
      reader.context.document.defaultView?.IntersectionObserver ??
      IntersectionObserver

    const frameObserver = new IntersectionObserverCtor(
      (entries) => {
        entries.forEach((entry) => {
          const frame = entry.target as HTMLIFrameElement
          const audios = Array.from(
            frame.contentDocument?.body.getElementsByTagName(`audio`) || [],
          )

          if (!entry.isIntersecting) {
            audios.forEach((audioElement) => {
              audioElement.pause()
              audioElement.currentTime = 0
            })
          } else {
            audios.forEach((audioElement) => {
              if (
                audioElement.hasAttribute(`autoplay`) &&
                audioElement.paused &&
                audioElement.readyState >= 2
              ) {
                audioElement.play().catch(console.error)
              }
            })
          }
        })
      },
      {
        threshold: 0.01,
      },
    )

    const elementObserver = new IntersectionObserverCtor(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target.tagName === `video`) {
            const video = entry.target as HTMLVideoElement
            if (!entry.isIntersecting) {
              video.pause()
              video.currentTime = 0
            } else {
              if (video.hasAttribute(`autoplay`)) {
                // this can fail when we play the first time due to user not having interacted with
                // document yet. Browsers policy. autoplay will play it the first time anyway.
                if (video.paused && video.readyState >= 2) {
                  video.play().catch(console.error)
                }
              }
            }
          }
        })
      },
      {
        threshold: 0.5,
      },
    )

    const cleanupByItemId = new Map<string, () => void>()

    reader.hookManager.register(`item.onDocumentLoad`, async ({ itemId }) => {
      cleanupByItemId.get(itemId)?.()
      cleanupByItemId.delete(itemId)

      const frame = reader.spineItemsManager
        .get(itemId)
        ?.renderer.getDocumentFrame()

      if (!frame) return

      frameObserver.observe(frame)

      const videos = frame.contentDocument?.body.getElementsByTagName(`video`)

      const unobserveElements = Array.from(videos || []).map((element) => {
        elementObserver.observe(element)

        return () => elementObserver.unobserve(element)
      })

      const cleanup = () => {
        frameObserver.unobserve(frame)
        unobserveElements.forEach((unobserve) => {
          unobserve()
        })
      }

      cleanupByItemId.set(itemId, cleanup)
    })

    reader.hookManager.register(`item.onDocumentUnload`, ({ itemId }) => {
      cleanupByItemId.get(itemId)?.()
      cleanupByItemId.delete(itemId)
    })

    const destroy = () => {
      frameObserver.disconnect()
      elementObserver.disconnect()
      cleanupByItemId.clear()
      reader.destroy()
    }

    return {
      ...reader,
      destroy,
    }
  }
